import { Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../app/stores/store";
import HomeLegend from "../common/HomeLegend";

type Align = 'right' | 'center' | 'left';

interface Column {
    id: keyof Data;
    label: string;
    minWidth?: number;
    align: Align;
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { 
        id: 'tagNumber',
        label: 'Tag Number',
        minWidth: 200,
        align: 'left'
    },
    { 
        id: 'system',
        label: 'System',
        minWidth: 200,
        align: 'left'
    },
    {
        id: 'recomendation',
        label: 'Recomendation',
        minWidth: 100,
        align: 'left',
    },
    {
        id: 'remark',
        label: 'Remark',
        minWidth: 50,
        align: 'center',
    },
];

interface Data {
    tagNumber: string;
    system: string;
    recomendation: string[];
    remark: {
        text: string;
        tooltip: string[]
    };
}

function createData(
    tagNumber: string,
    system: string,
    recomendation: string[],
    remark: string,
    hover: string[]
): Data {
    const combine = {
        text: remark,
        tooltip: hover,
    }
    
  return { tagNumber, system, recomendation, remark: combine };
}

const HomeAutoRecommendTable = () => {
    const theme = useTheme()
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rows, setRows] = useState<Data[]>([])
    const { getAutomationRecomendation, automationRecommendation, loadingAutomation } = useStore().homeAutoRecommendStore
    const { selectedStation } = useStore().commonStore
    
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    useEffect(() => {
        if (selectedStation) {
            getAutomationRecomendation(String(selectedStation.id))
        }
    }, [getAutomationRecomendation, selectedStation])

    useEffect(() => {
        automationRecommendation.forEach(i => {
            setRows(prev => [...prev, createData(i.tagNumber, i.systemName, i.recommendation, i.remark, i.hover)])
        })
        return () => setRows([])
    }, [automationRecommendation])

    if (loadingAutomation) {
        return (
            <Stack direction="row" justifyContent="center">
                <CircularProgress />
            </Stack>
        )
    }
    
    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '500px' }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                          {columns.map((column, x) => (
                            <TableCell
                                key={x}
                                align="center"
                                style={{ minWidth: column.minWidth }}
                            >
                                {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, x) => {
                              return (
                              <TableRow hover role="checkbox" tabIndex={-1} key={x}>
                                  {columns.map((column, idx) => {
                                    const value = row[column.id];
                                    return (
                                        <TableCell key={idx} align={column.align}>
                                            {typeof value === 'string' 
                                                ? value 
                                                : column.id === 'remark' 
                                                    ? (
                                                        <Tooltip 
                                                            placement="left"
                                                            title={
                                                                <>
                                                                    {(value as any).tooltip.map((i: string, b: number) => (
                                                                        <Typography
                                                                            key={b}
                                                                            fontSize="small" 
                                                                            sx={{color: 'white'}}
                                                                        >
                                                                            {i}
                                                                        </Typography>
                                                                    ))}
                                                                </>
                                                            } 
                                                        >
                                                            <Button
                                                                disableRipple
                                                                disableElevation
                                                                disableFocusRipple
                                                                disableTouchRipple
                                                                color="error"
                                                                sx={{
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {(value as any).text}
                                                            </Button>
                                                        </Tooltip>
                                                    )
                                                    : (
                                                        <ul>
                                                            {(value as string[]).map((i, t) => (
                                                                <li key={t}>{i}</li>
                                                            ))}
                                                        </ul>
                                                    )
                                            }
                                        </TableCell>
                                    );
                                  })}
                              </TableRow>
                              );
                          })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Stack pb={2} direction="row" justifyContent="center" spacing={2}>
                <HomeLegend color={theme.palette.error.main} label="Bad Actor: Reliability or Availability less than 90%"/>
            </Stack>
        </Paper>
    )
}

export default observer(HomeAutoRecommendTable)